import axios from 'axios';

const SESSION_KEY = 'kuesbTeacherBillingSession';
const REQUESTS_KEY = 'kuesbTeacherBillingRequests';
const activeRequests = new Map();
const baseUrl = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '');
export const billingUiEnabled = process.env.REACT_APP_AI_BILLING_ENABLED === 'true';
export const BILLING_UPDATED_EVENT = 'teacher-billing:updated';

export function notifyBillingUpdated() {
  if (typeof window?.dispatchEvent === 'function' && typeof window.Event === 'function') {
    window.dispatchEvent(new window.Event(BILLING_UPDATED_EVENT));
  }
}

export function clearBillingSession() {
  sessionStorage.removeItem(SESSION_KEY);
  notifyBillingUpdated();
}

export function saveBillingSession(session) {
  sessionStorage.removeItem(SESSION_KEY);
  if (session?.token) sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  notifyBillingUpdated();
}

export function getBillingSession() {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch (_) { return null; }
}

export function usableBalance(data) {
  const value = data?.wallet?.availableCredits ?? data?.availableCredits;
  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function requestFailure(code, message) {
  return Object.assign(new Error(message), { code });
}

function requireSession() {
  const current = getBillingSession();
  if (!current?.token || !Number.isFinite(current.expiresAt) || current.expiresAt <= Date.now()) {
    throw requestFailure('BILLING_LOGIN_REQUIRED', 'AI 기능을 이용하려면 선생님 계정으로 다시 로그인해 주세요.');
  }
  return current;
}

function requireSameSession(current) {
  if (getBillingSession()?.token !== current.token) {
    throw requestFailure('BILLING_LOGIN_REQUIRED', '로그인 계정이 변경되었습니다. 현재 계정에서 다시 실행해 주세요.');
  }
}

export async function billingRequest(action, body = {}) {
  const current = requireSession();
  const response = await axios.post(`${baseUrl}/billing/${action}`, body, {
    headers: { Authorization: `Bearer ${current.token}` }, timeout: 20000
  });
  requireSameSession(current);
  return response.data;
}

export function billingError(error) {
  const detail = error?.response?.data?.error;
  return (typeof detail === 'string' ? detail : detail?.message) || error?.message || 'AI 요청을 처리하지 못했습니다.';
}

async function fingerprint(identity, featureCode, input) {
  const value = new TextEncoder().encode(JSON.stringify([identity, featureCode, input]));
  const hash = await window.crypto.subtle.digest('SHA-256', value);
  return Array.from(new Uint8Array(hash), b => b.toString(16).padStart(2, '0')).join('');
}

function requestRecords() {
  try { return JSON.parse(localStorage.getItem(REQUESTS_KEY) || '{}'); } catch (_) { return {}; }
}

function persistRequest(key, value) {
  const requests = requestRecords();
  if (value) requests[key] = value; else delete requests[key];
  localStorage.setItem(REQUESTS_KEY, JSON.stringify(requests));
}

const featureLabels = {
  teacher_omr_extract: '모범답안 이미지 판독',
  teacher_omr_sheet_extract: '전체 모범답안지 판독',
};

// Preserve request IDs across retries without storing uploaded images in browser storage.
async function executeBilledAi(path, featureCode, body, config, key, current) {
  requireSameSession(current);
  let request = requestRecords()[key];
  if (!request?.quoteId) {
    const [quoteResponse, wallet] = await Promise.all([
      billingRequest('quote', { featureCode }), billingRequest('wallet'),
    ]);
    requireSameSession(current);
    const quote = quoteResponse.quote || quoteResponse;
    const credits = quote.credits;
    const quoteId = quoteResponse.quoteId || quote.quoteId;
    const available = usableBalance(wallet);
    if (!quoteId || !Number.isSafeInteger(credits) || credits < 0) {
      throw requestFailure('BILLING_INVALID_QUOTE', '예상 사용량을 확인하지 못했습니다.');
    }
    if (available === null) {
      throw requestFailure('BILLING_INVALID_BALANCE', '잔액을 확인하지 못했습니다. 다시 확인해 주세요.');
    }
    if (available < credits) {
      notifyBillingUpdated();
      throw requestFailure('INSUFFICIENT_BALANCE', `크레딧이 부족합니다. 현재 ${available.toLocaleString()}C / 필요 ${credits.toLocaleString()}C입니다. 홈페이지에서 충전해 주세요.`);
    }
    const confirmed = window.confirm(
      `${featureLabels[featureCode] || 'AI 작업'}\n내 잔액: ${available.toLocaleString()}C\n예상 사용량: ${credits.toLocaleString()}C${quote.mode === 'token' ? ' (최대 예약량)' : ''}\n선생님 본인의 크레딧으로 실행하시겠습니까?`
    );
    if (!confirmed) throw requestFailure('AI_CANCELED', 'AI 실행을 취소했습니다.');
    request = { requestId: request?.requestId || window.crypto.randomUUID(), quoteId, createdAt: request?.createdAt || Date.now() };
    persistRequest(key, request);
  }
  try {
    requireSameSession(current);
    const response = await axios.post(`${baseUrl}${path}`, body, { ...config, headers: {
      ...config.headers, Authorization: `Bearer ${current.token}`,
      'Idempotency-Key': request.requestId, 'X-Billing-Quote-Id': request.quoteId,
    } });
    requireSameSession(current);
    // Completed requests reuse their paid result even after an image-storage failure.
    return response;
  } catch (error) {
    const code = error.response?.data?.code || error.response?.data?.error?.code || error.code;
    if (code === 'AI_JOB_RELEASED' || code === 'BILLING_RESULT_EXPIRED') persistRequest(key, null);
    else if (['QUOTE_EXPIRED', 'QUOTE_CHANGED', 'QUOTE_MISMATCH', 'QUOTE_NOT_FOUND', 'INSUFFICIENT_BALANCE', 'PRICE_DISABLED'].includes(code)) {
      persistRequest(key, { ...request, quoteId: null });
    }
    throw requestFailure(code, billingError(error));
  } finally {
    // An AI charge may have completed even when its response or later image upload fails.
    notifyBillingUpdated();
  }
}

export async function submitBilledAi(path, featureCode, body, config = {}) {
  if (!billingUiEnabled) {
    throw requestFailure('BILLING_DISABLED', 'AI 크레딧 연결을 준비 중입니다. 설정 적용 후 다시 이용해 주세요.');
  }
  const current = requireSession();
  const activeKey = JSON.stringify([current.token, featureCode, body]);
  if (activeRequests.has(activeKey)) return activeRequests.get(activeKey);
  const pending = (async () => {
    const key = await fingerprint(current.identity, featureCode, body);
    return executeBilledAi(path, featureCode, body, config, key, current);
  })();
  activeRequests.set(activeKey, pending);
  try { return await pending; } finally { activeRequests.delete(activeKey); }
}
